import { useCallback, useRef } from 'react'
import { client } from '@Utils'
import { bookInfoEndpoints } from '@URLS'
import { errorMessage } from './contributors'

const TARGETS = {
  title: 'course-title',
  subtitle: 'subtitle',
}

// In-place editing of the server-rendered book title / subtitle: the element becomes
// contenteditable (plain text), Enter or blur saves, Escape restores. Titles are stored as
// plain text, so the edit starts from textContent and any markup in the old value is
// flattened on save (same tradeoff the ToC rename makes).
export function useInlineTextEdit({ coursenum, runMutation }) {
  const activeRef = useRef(null)

  const startEdit = useCallback(
    field => {
      if (activeRef.current) return
      const el = document.getElementById(TARGETS[field])
      if (!el) return

      const originalHtml = el.innerHTML
      const original = el.textContent.replace(/\s+/g, ' ').trim()
      activeRef.current = { el, field }

      el.classList.add('coverpage-inline-editing')
      el.contentEditable = 'plaintext-only'
      if (el.contentEditable !== 'plaintext-only') {
        el.contentEditable = 'true' // Firefox: fall back and force plain-text pastes below
      }
      el.textContent = original
      el.focus()
      try {
        document.getSelection().selectAllChildren(el)
      } catch (e) {
        // selection is a convenience only
      }

      const finish = () => {
        el.removeEventListener('keydown', onKeyDown)
        el.removeEventListener('blur', onBlur)
        el.removeEventListener('paste', onPaste)
        el.contentEditable = 'false'
        el.removeAttribute('contenteditable')
        el.classList.remove('coverpage-inline-editing')
        activeRef.current = null
      }
      const restore = () => {
        finish()
        el.innerHTML = originalHtml
        el.blur()
      }
      const save = () => {
        const value = el.textContent.replace(/\s+/g, ' ').trim()
        // nothing changed, or a title was blanked (titles can't be empty; subtitles can)
        if (value === original || (field === 'title' && !value)) {
          restore()
          return
        }
        finish()
        el.blur()
        runMutation(async () => {
          try {
            const resp = await client.post(
              bookInfoEndpoints.updateBookInfo({ coursenum }),
              { [field]: value }
            )
            const saved =
              resp.data && typeof resp.data[field] === 'string'
                ? resp.data[field]
                : value
            el.textContent = saved
          } catch (err) {
            el.innerHTML = originalHtml
            alert(errorMessage(err, 'The change could not be saved.'))
          }
        })
      }
      const onKeyDown = event => {
        if (event.key === 'Enter') {
          event.preventDefault()
          save()
        } else if (event.key === 'Escape') {
          event.preventDefault()
          restore()
        }
      }
      const onBlur = () => save()
      const onPaste = event => {
        event.preventDefault()
        const text = (event.clipboardData || window.clipboardData).getData(
          'text/plain'
        )
        document.execCommand('insertText', false, text.replace(/\s+/g, ' '))
      }
      el.addEventListener('keydown', onKeyDown)
      el.addEventListener('blur', onBlur)
      el.addEventListener('paste', onPaste)
    },
    [coursenum, runMutation]
  )

  return { startEdit }
}
